import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Form, Input, InputNumber, Popconfirm, Select, Space, Typography } from "antd";
import { type ChangeEvent, useEffect, useState } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { validatePhone } from "@/utils/validate";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";

interface GroupCompanyFile {
    id?: number;
    path?: string;
}

export interface GroupCompanyRow {
    groupCompany?: {
        id?: number | string;
        name?: string;
        contactName?: string;
        contactPhone?: string;
        imgMainId?: number;
        showLocation?: number;
        createdTime?: string;
    };
    mainFile?: GroupCompanyFile;
    supplierList?: unknown[];
}

export interface GroupCompanyPageResult {
    list?: GroupCompanyRow[];
    total?: number;
}

export interface GroupCompanyFormValues {
    name: string;
    contactName?: string;
    contactPhone?: string;
    imgMainId: number;
    showLocation?: number;
}

export interface GroupCompanyPageProps {
    loadGroups?: (query: { page: number; size: number; keyword?: string }) => Promise<GroupCompanyPageResult>;
    createGroup?: (values: GroupCompanyFormValues) => Promise<unknown>;
    updateGroup?: (id: number | string, values: GroupCompanyFormValues) => Promise<unknown>;
    deleteGroup?: (id: number | string) => Promise<unknown>;
    permissions?: string[];
}

// 沿用旧 Vue useTable 的默认分页大小，集团列表首次加载和重置均按每页 10 条请求。
const defaultQuery = { page: 1, size: 10 };
const showLocationLabels: Record<number, string> = {
    1: "广告位1",
    2: "广告位2",
    3: "广告位3",
    4: "广告位4",
    5: "广告位5",
    6: "广告位6",
    7: "广告位7",
    8: "广告位8"
};
const GROUP_COMPANY_LOGO_MAX_SIZE = 3 * 1024 * 1024;

/** 按旧 Vue 集团列表的 DATETIME_PICKER_FORMAT 展示创建时间。 */
function formatGroupCompanyCreatedTime(value?: string): string {
    return dateFormat(value, DATETIME_PICKER_FORMAT);
}

/** 按 Vue single-file-upload 契约校验集团 logo 的扩展名、MIME 类型和大小。 */
function validateGroupCompanyLogo(file: File): string | null {
    const extension = file.name.split(".").pop()?.toLowerCase();
    const validType =
        (extension === "jpg" && file.type === "image/jpeg") || (extension === "png" && file.type === "image/png");
    if (!validType) return "集团logo仅支持 jpg/png 格式";
    if (file.size > GROUP_COMPANY_LOGO_MAX_SIZE) return "集团logo大小不能超过3M";
    return null;
}

/** 复用 Vue single-file-upload 的 FormData.file 上传字段，并返回后端文件 ID。 */
export async function uploadGroupCompanyLogo(file: Blob): Promise<number> {
    const formData = new FormData();
    formData.append("file", file);
    const { fileService } = await import("@/api/services/fileService");
    const uploadedFile = await fileService.uploadFile(formData);
    return Number(uploadedFile.id);
}

/** 复用 Vue 集团管理分页接口。 */
export async function loadGroupCompanyPage(query: { page: number; size: number; keyword?: string }) {
    const response = await request.service.get<GroupCompanyPageResult>("/admin/group-company/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 集团新增接口。 */
export async function createGroupCompany(values: GroupCompanyFormValues) {
    const response = await request.service.post("/admin/group-company", values);
    return response.data;
}

/** 复用 Vue 集团编辑接口。 */
export async function updateGroupCompany(id: number | string, values: GroupCompanyFormValues) {
    const response = await request.service.put(`/admin/group-company/${id}`, values);
    return response.data;
}

/** 复用 Vue 集团删除接口。 */
export async function deleteGroupCompany(id: number | string) {
    const response = await request.service.delete(`/admin/group-company/${id}`);
    return response.data;
}

/** 集团管理列表，迁移 Vue 集团 logo、关联状态、筛选和核心操作。 */
export function GroupCompanyPage({
    loadGroups: load = loadGroupCompanyPage,
    createGroup = createGroupCompany,
    updateGroup = updateGroupCompany,
    deleteGroup = deleteGroupCompany,
    permissions
}: GroupCompanyPageProps) {
    const [rows, setRows] = useState<GroupCompanyRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [dialogRow, setDialogRow] = useState<GroupCompanyRow | null | undefined>(undefined);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [form] = Form.useForm<GroupCompanyFormValues>();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeButtonBanned = useUserStore(state => state.banButton);
    // 路由未注入页面权限时沿用登录用户权限，保持生产路由与独立页面测试的同一行为。
    const currentPermissions = permissions ?? storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    const canCustomizeWrite = currentPermissions.includes("ROLE_CUSTOMIZED_W");
    // 沿用 Vue ROLE_BUTTON_BAN：保留写入口但禁用所有会改变数据的控件。
    const buttonBanned = currentPermissions.includes("ROLE_BUTTON_BAN") || (!permissions && storeButtonBanned);

    const refresh = async (nextQuery = query) => {
        const result = await load(nextQuery);
        setRows(result.list || []);
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("集团加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    const openCreate = () => {
        form.resetFields();
        setDialogRow(null);
    };

    const openEdit = (row: GroupCompanyRow) => {
        form.setFieldsValue({
            name: row.groupCompany?.name,
            contactName: row.groupCompany?.contactName,
            contactPhone: row.groupCompany?.contactPhone,
            imgMainId: row.groupCompany?.imgMainId,
            showLocation: row.groupCompany?.showLocation
        });
        setDialogRow(row);
    };

    /** 上传集团 logo 并将成功响应中的文件 ID 回填到现有保存表单。 */
    const handleLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = "";
        if (!file) return;

        const validationError = validateGroupCompanyLogo(file);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        setUploadingLogo(true);
        setErrorMessage(null);
        try {
            const fileId = await uploadGroupCompanyLogo(file);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("missing file id");
            form.setFieldsValue({ imgMainId: fileId });
        } catch {
            setErrorMessage("集团logo上传失败");
        } finally {
            setUploadingLogo(false);
        }
    };

    const submitForm = async () => {
        try {
            const values = await form.validateFields();
            if (dialogRow?.groupCompany?.id != null) {
                await updateGroup(dialogRow.groupCompany.id, values);
            } else {
                await createGroup(values);
            }
            setDialogRow(undefined);
            setErrorMessage(null);
            await refresh();
        } catch (error) {
            if (error && typeof error === "object" && "errorFields" in error) return;
            setErrorMessage("集团保存失败");
        }
    };

    const removeGroup = async (id?: number | string) => {
        if (id == null) return;
        try {
            await deleteGroup(id);
            await refresh();
            setErrorMessage(null);
        } catch {
            setErrorMessage("集团删除失败");
        }
    };

    const submitSearch = () => {
        setQuery({ ...defaultQuery, ...(keyword.trim() ? { keyword: keyword.trim() } : {}) });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 集团 ProTable 的嵌套字段和操作插槽。
    const columns: TableColumnProps<GroupCompanyRow>[] = [
        { key: "id", label: "序号", width: 78, prop: "groupCompany", render: ({ row }) => row.groupCompany?.id || "-" },
        {
            key: "name",
            label: "集团名称",
            minWidth: 206,
            prop: "groupCompany",
            render: ({ row }) => row.groupCompany?.name || "--"
        },
        {
            key: "logo",
            label: "集团logo",
            width: 148,
            prop: "mainFile",
            render: ({ row }) =>
                row.mainFile?.path ? (
                    <img alt="集团logo" height={65} src={row.mainFile.path} style={{ objectFit: "cover" }} width={65} />
                ) : (
                    "--"
                )
        },
        {
            key: "showLocation",
            label: "展示位置",
            minWidth: 180,
            prop: "groupCompany",
            render: ({ row }) => showLocationLabels[Number(row.groupCompany?.showLocation)] || "--"
        },
        {
            key: "contactName",
            label: "联系人",
            minWidth: 156,
            prop: "groupCompany",
            render: ({ row }) => row.groupCompany?.contactName || "--"
        },
        {
            key: "contactPhone",
            label: "联系电话",
            width: 162,
            prop: "groupCompany",
            render: ({ row }) => row.groupCompany?.contactPhone || "--"
        },
        {
            key: "supplierList",
            label: "是否关联供应商",
            minWidth: 169,
            prop: "supplierList",
            render: ({ row }) => (row.supplierList?.length ? "是" : "否")
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 141,
            prop: "groupCompany",
            render: ({ row }) => formatGroupCompanyCreatedTime(row.groupCompany?.createdTime)
        },
        ...(canCustomizeWrite
            ? [
                  {
                      key: "operation",
                      label: "操作",
                      width: 240,
                      prop: "operation",
                      fixed: "right" as const,
                      render: ({ row }: { row: GroupCompanyRow }) =>
                          canWrite ? (
                              <Space>
                                  <Button
                                      aria-label="编辑集团"
                                      disabled={buttonBanned}
                                      size="small"
                                      onClick={() => openEdit(row)}
                                  >
                                      修改
                                  </Button>
                                  {!row.supplierList?.length ? (
                                      <Popconfirm
                                          cancelText="取消"
                                          okText="确定"
                                          title="确认删除该集团吗？"
                                          onConfirm={() => void removeGroup(row.groupCompany?.id)}
                                      >
                                          <Button aria-label="删除集团" danger disabled={buttonBanned} size="small">
                                              删除
                                          </Button>
                                      </Popconfirm>
                                  ) : null}
                              </Space>
                          ) : null
                  }
              ]
            : [])
    ];

    return (
        <section aria-label="group-company-page">
            <Card>
                <ZhTable<GroupCompanyRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无集团"
                    pagination={false}
                    rowKey={row => String(row.groupCompany?.id || row.groupCompany?.name)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                集团管理
                            </Typography.Title>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}

                            <Space className="admin-table-filter-form mb-4">
                                {canWrite ? (
                                    <Button
                                        className="group-company-create-button"
                                        disabled={buttonBanned}
                                        type="primary"
                                        onClick={openCreate}
                                    >
                                        新增集团
                                    </Button>
                                ) : null}
                                <Input
                                    className="group-company-keyword"
                                    placeholder="请输入集团名称搜索"
                                    value={keyword}
                                    onChange={event => setKeyword(event.target.value)}
                                />
                                <Button
                                    className="group-company-search-button"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={submitSearch}
                                >
                                    搜索
                                </Button>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                hideOnSinglePage
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
                <ZhDialog
                    open={dialogRow !== undefined}
                    title={dialogRow ? "编辑集团" : "新增集团"}
                    width={480}
                    onCancel={() => setDialogRow(undefined)}
                    footer={
                        <Space>
                            <Button onClick={() => setDialogRow(undefined)}>取消</Button>
                            <Button aria-label="提交集团" type="primary" onClick={() => void submitForm()}>
                                提交
                            </Button>
                        </Space>
                    }
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            label="集团名称"
                            name="name"
                            rules={[
                                { required: true, message: "请输入集团名称" },
                                { max: 32, message: "不超过32位字符" }
                            ]}
                        >
                            <Input placeholder="请输入集团名称" />
                        </Form.Item>
                        <Form.Item label="联系人" name="contactName">
                            <Input placeholder="请输入联系人" />
                        </Form.Item>
                        <Form.Item
                            label="联系电话"
                            name="contactPhone"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        !value || validatePhone(String(value))
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("请输入正确的手机号")),
                                    message: "请输入正确的手机号"
                                }
                            ]}
                        >
                            <Input placeholder="请输入联系电话" />
                        </Form.Item>
                        <Form.Item label="集团logo" required>
                            <input
                                accept=".jpg,.png,image/jpeg,image/png"
                                aria-label="上传集团logo"
                                disabled={buttonBanned || uploadingLogo}
                                type="file"
                                onChange={event => void handleLogoChange(event)}
                            />
                            <div>支持上传jpg文件和png文件，最大不超过3M</div>
                            <Form.Item noStyle name="imgMainId" rules={[{ required: true, message: "请上传集团logo" }]}>
                                <InputNumber className="w-full" min={1} placeholder="请输入集团logo文件ID" />
                            </Form.Item>
                        </Form.Item>
                        <Form.Item label="展示位置" name="showLocation">
                            <Select
                                allowClear
                                placeholder="请选择展示位置"
                                options={Object.entries(showLocationLabels).map(([value, label]) => ({
                                    value: Number(value),
                                    label
                                }))}
                            />
                        </Form.Item>
                    </Form>
                </ZhDialog>
            </Card>
        </section>
    );
}

export default GroupCompanyPage;
